/**
 * LinkedIn Integration OAuth Scopes (Story 10.1, Task 2)
 *
 * Maps each LinkedIn platform to its required permissions.
 * LinkedIn requests ALL scopes in a single consent — same pattern as Meta.
 *
 * Key differences from Meta:
 * - Space-delimited scopes (not comma-separated)
 * - Only 2 platforms (not 4)
 * - Scopes are permission strings, not URL-based
 */

import type { Platform } from "@provenleap/db";

// ============================================================================
// Types
// ============================================================================

export interface LinkedInScopeConfig {
  /** LinkedIn permission name(s) required for this platform */
  readonly scopes: readonly string[];
}

/**
 * LinkedIn platform values for validation.
 */
export const LINKEDIN_PLATFORM_VALUES = [
  "linkedin-pages",
  "linkedin-ads",
] as const;

export type LinkedInPlatform = (typeof LINKEDIN_PLATFORM_VALUES)[number];

// ============================================================================
// Constants
// ============================================================================

/**
 * LinkedIn API version header value.
 * All LinkedIn API calls MUST include `LinkedIn-Version: {version}` header.
 * Each monthly version sunsets 12 months after release.
 */
export const LINKEDIN_API_VERSION = "202603";

// ============================================================================
// Scope Mapping
// ============================================================================

/**
 * Maps each LinkedIn platform to its required permission(s).
 *
 * - linkedin-pages: Organization social + admin analytics (followers, visitors, content)
 * - linkedin-ads: Ad account read + reporting
 */
export const LINKEDIN_INTEGRATION_SCOPES: Record<LinkedInPlatform, LinkedInScopeConfig> = {
  "linkedin-pages": {
    scopes: ["r_organization_social", "rw_organization_admin"],
  },
  "linkedin-ads": {
    scopes: ["r_ads", "r_ads_reporting"],
  },
} as const;

/** All LinkedIn platforms that use OAuth */
export const LINKEDIN_OAUTH_PLATFORMS = Object.keys(
  LINKEDIN_INTEGRATION_SCOPES
) as Platform[];

/** All unique LinkedIn scopes as a flat array — derived from LINKEDIN_INTEGRATION_SCOPES to prevent drift */
export const ALL_LINKEDIN_SCOPES = [
  ...new Set(
    Object.values(LINKEDIN_INTEGRATION_SCOPES).flatMap((c) => c.scopes)
  ),
];

// ============================================================================
// Scope Utilities
// ============================================================================

/**
 * Determines which LinkedIn platforms are authorized based on granted scopes.
 *
 * Returns platform keys for platforms where ALL required scopes have been granted.
 * If user deselects permissions, only authorized platforms are returned.
 */
export function getEnabledLinkedInPlatforms(grantedScopes: string[]): LinkedInPlatform[] {
  const grantedSet = new Set(grantedScopes);
  const enabled: LinkedInPlatform[] = [];

  for (const [platform, config] of Object.entries(LINKEDIN_INTEGRATION_SCOPES)) {
    const allScopesGranted = config.scopes.every((s) => grantedSet.has(s));
    if (allScopesGranted) {
      enabled.push(platform as LinkedInPlatform);
    }
  }

  return enabled;
}

/**
 * Detects scope reduction when a user re-authorizes LinkedIn and
 * deselects previously granted permissions.
 *
 * Returns removed scopes and which platforms are affected.
 */
export function detectLinkedInScopeReduction(
  previousScopes: string[],
  newScopes: string[]
): { removed: string[]; affectedPlatforms: LinkedInPlatform[] } {
  const newSet = new Set(newScopes);
  const removed = previousScopes.filter((s) => !newSet.has(s));

  if (removed.length === 0) {
    return { removed: [], affectedPlatforms: [] };
  }

  const removedSet = new Set(removed);
  const affectedPlatforms: LinkedInPlatform[] = [];

  for (const platform of LINKEDIN_PLATFORM_VALUES) {
    const config = LINKEDIN_INTEGRATION_SCOPES[platform];
    if (config) {
      const hasRemovedScope = config.scopes.some((s) => removedSet.has(s));
      if (hasRemovedScope) {
        affectedPlatforms.push(platform);
      }
    }
  }

  return { removed, affectedPlatforms };
}
